import * as THREE from 'three';
import type { HazardDef } from '../game/content';
import { t } from '../i18n';
import type { World } from '../game/World';
import { rgb } from '../utils/color';
import type { Enemy } from './Enemies';

interface Danger {
  active: boolean;
  x: number;
  z: number;
  r: number;
  delay: number;
  damage: number;
  hurtsEnemies: boolean;
  killer: string;
  color: THREE.Color;
}

interface Field {
  active: boolean;
  kind: 'curse' | 'rift';
  x: number;
  z: number;
  r: number;
  life: number;
  tick: number;
}

export interface HazardForces {
  pushX: number;
  pushZ: number;
  slow: number;
}

// Telegraphed danger zones (used by bosses and stage hazards) plus the per-stage hazard driver.
export class Hazards {
  private readonly dangers: Danger[] = [];
  private readonly fields: Field[] = [];
  private def: HazardDef | null = null;
  private timer = 0;
  private gustTime = 0;
  private gustX = 0;
  private gustZ = 0;
  private warned = false;
  private sandTick = 0;
  private rockWarned = false;
  private readonly forces: HazardForces = { pushX: 0, pushZ: 0, slow: 0 };
  private readonly buf: Enemy[] = new Array(420);
  private readonly tmp = new THREE.Color();

  constructor(private readonly world: World) {
    for (let i = 0; i < 48; i += 1) this.dangers.push({ active: false, x: 0, z: 0, r: 1, delay: 0, damage: 0, hurtsEnemies: false, killer: 'killer.ground', color: new THREE.Color() });
    for (let i = 0; i < 8; i += 1) this.fields.push({ active: false, kind: 'curse', x: 0, z: 0, r: 1, life: 0, tick: 0 });
  }

  reset(def: HazardDef | null): void {
    this.def = def;
    this.timer = def ? def.start : 0;
    this.gustTime = 0;
    this.warned = false;
    this.sandTick = 0;
    this.rockWarned = false;
    for (const d of this.dangers) d.active = false;
    for (const f of this.fields) f.active = false;
  }

  get windActive(): boolean {
    return this.gustTime > 0;
  }

  get wind(): { x: number; z: number } {
    return { x: this.gustX, z: this.gustZ };
  }

  dangerZone(x: number, z: number, r: number, delay: number, damage: number, color: THREE.Color, hurtsEnemies: boolean, killer = 'killer.ground'): void {
    const d = this.dangers.find((dd) => !dd.active);
    if (!d) return;
    d.active = true;
    d.x = x;
    d.z = z;
    d.r = r;
    d.delay = delay;
    d.damage = damage;
    d.hurtsEnemies = hurtsEnemies;
    d.killer = killer;
    d.color.copy(color);
    this.world.shapes.telegraph(x, z, r, color, delay);
  }

  update(dt: number): HazardForces {
    const w = this.world;
    const f = this.forces;
    f.pushX = f.pushZ = f.slow = 0;
    this.updateDangers(dt);
    this.updateFields(dt, f);
    if (!this.def || w.time < this.def.start) return f;

    const def = this.def;
    const px = w.player.x;
    const pz = w.player.z;
    if (this.gustTime > 0) {
      this.gustTime -= dt;
      const strength = 2.8 * Math.min(1, this.gustTime, 6 - this.gustTime);
      f.pushX += this.gustX * strength;
      f.pushZ += this.gustZ * strength;
      // A sandstorm also scours: one small hit a second, without i-frames, so standing in it never shields the player.
      if (def.kind === 'sandstorm') {
        this.sandTick -= dt;
        if (this.sandTick <= 0) {
          this.sandTick = 1;
          w.hurtPlayer(def.damage, 'killer.sandstorm', false);
        }
      }
      for (const e of w.enemies.active) {
        if (!e.alive || e.boss || e.def.behavior === 'static') continue;
        e.x += this.gustX * strength * dt * 0.8;
        e.z += this.gustZ * strength * dt * 0.8;
      }
      if (!w.reducedFx) {
        for (let k = 0; k < 5; k += 1) {
          const ox = px - this.gustX * 16 + (w.rng() - 0.5) * 30;
          const oz = pz - this.gustZ * 16 + (w.rng() - 0.5) * 30;
          w.particles.emit(ox, 0.5 + w.rng() * 3, oz, this.gustX * 22, -0.5, this.gustZ * 22, def.kind === 'sandstorm' ? this.tmp.setRGB(1.6, 1.3, 0.8) : this.tmp.setRGB(1.4, 1.5, 1.7), 0.25, 1.2, 0, 0);
        }
      }
    }

    this.timer -= dt;
    if ((def.kind === 'blizzard' || def.kind === 'sandstorm') && !this.warned && this.timer < 1.5) {
      this.warned = true;
      if (def.kind === 'sandstorm') w.banner(t('banner.sandstorm'), t('banner.sandstormSub'), '#ffd08a');
      else w.banner(t('banner.blizzard'), t('banner.blizzardSub'), '#dff6ff');
    }
    if (this.timer > 0) return f;
    this.timer = def.interval;
    this.warned = false;
    const c = this.tmp.set(def.color);
    switch (def.kind) {
      case 'eruption':
      case 'rockfall':
        if (def.kind === 'rockfall' && !this.rockWarned) {
          this.rockWarned = true;
          w.banner(t('banner.rockfall'), t('banner.rockfallSub'), '#c9b79a');
        }
        for (let k = 0; k < 3; k += 1) {
          const a = w.rng() * Math.PI * 2;
          const r = k === 0 ? w.rng() * 2.5 : 3 + w.rng() * 6;
          this.dangerZone(px + Math.cos(a) * r, pz + Math.sin(a) * r, 2.2, 1.3, def.damage, c, true, def.kind === 'rockfall' ? 'killer.rockfall' : 'killer.ground');
        }
        break;
      case 'lightning':
        for (let k = 0; k < 2; k += 1) {
          const lead = k === 0 ? 1.0 : 0;
          const a = w.rng() * Math.PI * 2;
          const r = k === 0 ? 0 : 2 + w.rng() * 7;
          this.dangerZone(px + w.player.vx * lead + Math.cos(a) * r, pz + w.player.vz * lead + Math.sin(a) * r, 1.7, 1.0, def.damage, c, true);
        }
        break;
      case 'blizzard':
      case 'sandstorm': {
        const a = w.rng() * Math.PI * 2;
        this.gustX = Math.cos(a);
        this.gustZ = Math.sin(a);
        this.gustTime = 6;
        w.audio.wind();
        break;
      }
      case 'curse':
      case 'rift': {
        const field = this.fields.find((ff) => !ff.active);
        if (!field) break;
        const ahead = Math.atan2(w.player.vz, w.player.vx);
        const moving = Math.hypot(w.player.vx, w.player.vz) > 0.5;
        const a = moving ? ahead + (w.rng() - 0.5) * 1.2 : w.rng() * Math.PI * 2;
        const r = def.kind === 'rift' ? 7 + w.rng() * 3 : 4 + w.rng() * 4;
        field.active = true;
        field.kind = def.kind;
        field.x = px + Math.cos(a) * r;
        field.z = pz + Math.sin(a) * r;
        field.r = def.kind === 'rift' ? 1.3 : 2.6;
        field.life = def.kind === 'rift' ? 7 : 9;
        field.tick = 0;
        if (def.kind === 'rift') {
          w.shapes.disc(field.x, field.z, 0.5, 3.2, c, field.life, 0.9, 1, 2);
          w.shapes.ring(field.x, field.z, 3.4, 0.6, c, field.life, 1.8);
          w.banner(t('banner.rift'), t('banner.riftSub'), '#ff7af2');
        } else {
          w.shapes.disc(field.x, field.z, field.r * 0.7, field.r, c, field.life, 0.75, 1, 0.4);
          w.shapes.ring(field.x, field.z, field.r, field.r, c, field.life, 1.2);
        }
        break;
      }
    }
    return f;
  }

  private updateDangers(dt: number): void {
    const w = this.world;
    for (const d of this.dangers) {
      if (!d.active) continue;
      d.delay -= dt;
      if (d.delay > 0) continue;
      d.active = false;
      const dx = w.player.x - d.x;
      const dz = w.player.z - d.z;
      if (Math.hypot(dx, dz) < d.r + w.player.radius * 0.5) w.hurtPlayer(d.damage, d.killer);
      if (d.hurtsEnemies) {
        const n = w.enemies.queryCircle(d.x, d.z, d.r, this.buf);
        for (let i = 0; i < n; i += 1) {
          const e = this.buf[i];
          if (!e.boss) w.enemies.applyDamage(e, d.damage * 3, 0, 0, 0);
        }
      }
      const c = this.tmp.copy(d.color);
      w.shapes.disc(d.x, d.z, d.r * 0.5, d.r * 1.1, c, 0.35, 0.9);
      w.shapes.ring(d.x, d.z, d.r * 0.6, d.r * 1.3, c, 0.3, 1.5);
      const bright = c.clone().multiplyScalar(2);
      if (this.def?.kind === 'lightning') {
        for (let k = 0; k < 18; k += 1) w.particles.emit(d.x + (w.rng() - 0.5) * 0.4, k * 0.7, d.z + (w.rng() - 0.5) * 0.4, 0, 0, 0, bright, 0.9, 0.25, 0, 0);
        w.audio.thunder();
      } else {
        w.particles.burst(d.x, 0.3, d.z, w.reducedFx ? 8 : 24, bright, 4, 0.7, 0.7, 2.2, -6);
        w.audio.explode(0.45);
      }
      w.shake(0.12);
    }
  }

  private updateFields(dt: number, f: HazardForces): void {
    const w = this.world;
    for (const field of this.fields) {
      if (!field.active) continue;
      field.life -= dt;
      if (field.life <= 0) {
        field.active = false;
        continue;
      }
      const dx = field.x - w.player.x;
      const dz = field.z - w.player.z;
      const d = Math.hypot(dx, dz) || 0.01;
      field.tick -= dt;
      if (field.kind === 'curse') {
        if (d < field.r) {
          f.slow = Math.max(f.slow, 0.4);
          if (field.tick <= 0) {
            field.tick = 0.5;
            w.hurtPlayer(this.def?.damage ?? 5, 'killer.curse');
          }
        }
        if (!w.reducedFx && w.rng() < 0.4) {
          const a = w.rng() * Math.PI * 2;
          const r = Math.sqrt(w.rng()) * field.r;
          w.particles.emit(field.x + Math.cos(a) * r, 0.1, field.z + Math.sin(a) * r, 0, 0.8, 0, this.tmp.copy(rgb('#a06bff')).multiplyScalar(1.5), 0.6, 1.2, 0, 0.5);
        }
      } else {
        const reach = 12;
        if (d < reach) {
          const pull = 4.2 * (1 - d / reach);
          f.pushX += (dx / d) * pull;
          f.pushZ += (dz / d) * pull;
        }
        if (d < field.r && field.tick <= 0) {
          field.tick = 0.5;
          w.hurtPlayer(this.def?.damage ?? 10, 'killer.rift');
        }
        if (!w.reducedFx) {
          const a = w.rng() * Math.PI * 2;
          w.particles.emit(field.x + Math.cos(a) * 5, 0.4, field.z + Math.sin(a) * 5, -Math.cos(a) * 8, 0.3, -Math.sin(a) * 8, this.tmp.copy(rgb('#ff3df2')).multiplyScalar(2), 0.4, 0.6, 0, 0);
        }
      }
    }
  }
}
